import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  TextField,
  Typography,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import { Delete, Lock } from "@mui/icons-material";
import { IRaffle } from "../../../store/interfaces/IRaffle";
import {
  useBlockNumberMutation,
  useGetBlockedNumbersByRaffleIdQuery,
  useLazyGetRaffleWinnerQuery,
  useUnblockNumberMutation,
} from "../../../store/api";
import { enqueueSnackbar } from "notistack";
import { Award } from "lucide-react";

interface BlockedNumbersProps {
  raffle: IRaffle;
}

export const BlockedNumbers = ({ raffle }: BlockedNumbersProps) => {
  const [getAssignedNumber] = useLazyGetRaffleWinnerQuery();
  const { data: currentBlockedNumbers, refetch } = useGetBlockedNumbersByRaffleIdQuery(raffle.id, {
    refetchOnFocus: false,
  });

  const [blockNumber, { data: blockNumberResponse }] = useBlockNumberMutation();
  const [unblockNumber, { data: unblockNumberResponse }] = useUnblockNumberMutation();

  const [newBlockedNumber, setNewBlockedNumber] = useState<number | undefined>();
  const [newUnblockedNumber, setNewUnblockedNumber] = useState<number | undefined>();
  const [openUnblockDialog, setOpenUnblockDialog] = useState(false);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (blockNumberResponse) {
      enqueueSnackbar(`El número ${newBlockedNumber} ha sido bloqueado correctamente`, { variant: "success" });
      setNewBlockedNumber(undefined);
      refetch();
    }
  }, [blockNumberResponse]);

  useEffect(() => {
    if (unblockNumberResponse) {
      enqueueSnackbar(`El número ${newUnblockedNumber} ha sido desbloqueado correctamente`, { variant: "success" });
      setNewUnblockedNumber(undefined);
      setOpenUnblockDialog(false);
      refetch();
    }
  }, [unblockNumberResponse]);

  const handleUnblockNumber = () => {
    if (newUnblockedNumber !== undefined) {
      unblockNumber({ newNumber: newUnblockedNumber, raffleId: raffle.id });
    }
  };

  const handleAddBlockedNumber = async () => {
    if (newBlockedNumber !== undefined && newBlockedNumber >= 0) {
      const result = await getAssignedNumber({ raffleId: raffle.id, ticket: newBlockedNumber }) as any;

      if (result.isError && result.error?.status === 404) {
        enqueueSnackbar(`El número ${newBlockedNumber} no está asignado. Se bloqueará igualmente.`, { variant: "info" });
        blockNumber({ newNumber: newBlockedNumber, raffleId: raffle.id });
      } else if (!result.isError && result.status === "fulfilled") {
        enqueueSnackbar(`El número ${newBlockedNumber} ya está asignado a ${result.data.name}`, { variant: "error" });
      } else {
        enqueueSnackbar(`Error: ${JSON.stringify(result.error)}`, { variant: "error" });
      }
    }
  };

  return (
    <Card sx={{ mx: "auto", p: 3, borderRadius: 3, boxShadow: 4, maxWidth: 900 }}>
      <CardContent sx={{ position: "relative", overflow: "hidden" }}>
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h5" color="primary" gutterBottom>
            <Award size={20} style={{ verticalAlign: "middle", marginRight: 8 }} />
            Números premiados
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Puedes seleccionar hasta <strong>8 números</strong>.
          </Typography>
        </Box>


        <Dialog open={openUnblockDialog}>
          <DialogTitle>¿Liberar el número {newUnblockedNumber}?</DialogTitle>
          <DialogContent>
            <Typography>Una vez liberado, el número podrá ser reasignado a un comprador.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenUnblockDialog(false)}>Cancelar</Button>
            <Button onClick={handleUnblockNumber} variant="contained" color="primary">Confirmar</Button>
          </DialogActions>
        </Dialog>

        <Paper elevation={2} sx={{ p: 3, mb: 4, mt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              variant="outlined"
              inputProps={{ maxLength: raffle.limit.toString().length - 1 }}
              value={newBlockedNumber ?? ""}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setNewBlockedNumber(value ? Number(value) : undefined);
              }}
            />

            <Button
              variant="contained"
              color="primary"
              disabled={
                !newBlockedNumber ||
                (currentBlockedNumbers?.includes(newBlockedNumber) ?? false) ||
                (currentBlockedNumbers?.length ?? 0) >= 8
              }
              onClick={handleAddBlockedNumber}
              endIcon={<Lock />}
              sx={{ fontWeight: "bold", fontSize: 16 }}
            >
              Seleccionar número {newBlockedNumber ?? ""}
            </Button>

            {(currentBlockedNumbers?.length ?? 0) >= 8 && (
              <Alert severity="warning">
                Ya has seleccionado el máximo de 8 números. Deselecciona uno para continuar.
              </Alert>
            )}
          </Stack>
        </Paper>

        <Box>
          {currentBlockedNumbers?.length === 0 ? (
            <Alert severity="info">No hay números seleccionados.</Alert>
          ) : (
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Números seleccionados:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {currentBlockedNumbers && currentBlockedNumbers.map((blockedNumber) => (
                  <Chip
                    key={blockedNumber}
                    label={`#${blockedNumber}`}
                    onDelete={() => {
                      setNewUnblockedNumber(blockedNumber);
                      setOpenUnblockDialog(true);
                    }}
                    deleteIcon={<Delete />}
                    variant="outlined"
                    color="primary"
                    sx={{ fontSize: 16 }}
                  />
                ))}
              </Stack>
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
